import { cn } from '@/lib/utils';

const fmtHours = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/**
 * Reports redesign — per-day activity bars rendered inline in
 * project rows. Each bar's height is proportional to the day's
 * minutes vs the column's max; days with 0 minutes render as a
 * thin horizontal divider line so the row's grid stays visually
 * intact (matches the prototype's `.is-empty` modifier).
 *
 * `weekendIndices` lets the caller mark Saturdays/Sundays as
 * dimmed; pass `null` to skip the dimming (e.g. when the date
 * mapping isn't available). The 26px height matches the
 * prototype's `.v1-rep-spark` container.
 */
export interface SparkbarsProps {
  /** Daily minutes, dense, ordered from `from` to `to`. */
  days: ReadonlyArray<number>;
  /** Bar fill color. Caller passes the project's hue. */
  color?: string;
  /** Indexes (into `days`) that fall on a weekend. Null disables dim. */
  weekendIndices?: ReadonlyArray<number> | null;
  className?: string;
}

export const Sparkbars = ({
  days,
  color = 'oklch(0.6 0.1 245 / 0.4)',
  weekendIndices = null,
  className,
}: SparkbarsProps) => {
  const max = Math.max(...days, 1);
  const weekendSet = weekendIndices ? new Set(weekendIndices) : null;

  return (
    <div
      role="img"
      aria-label={`Daily activity, max ${fmtHours(max)}`}
      className={cn('flex h-[26px] w-full min-w-0 items-end gap-[2px]', className)}
    >
      {days.map((minutes, i) => {
        if (minutes === 0) {
          return (
            <span
              key={i}
              className="h-px flex-1 self-center rounded-[1.5px] bg-[var(--glass-card-border)]"
            />
          );
        }
        const heightPx = Math.max(2, (minutes / max) * 26);
        const isWeekend = weekendSet?.has(i) ?? false;
        return (
          <span
            key={i}
            title={`Day ${i + 1}: ${fmtHours(minutes)}`}
            className={cn(
              'flex-1 rounded-[1.5px] transition-colors duration-100',
              isWeekend && 'opacity-60',
            )}
            style={{ height: heightPx, background: color }}
          />
        );
      })}
    </div>
  );
};
